import { Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COLOR_OPTIONS, handleImageUpload } from "./admin-utils";

export function CategoryModal({ open, onOpenChange, editingCategory, categoryForm, setCategoryForm, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="cat-label">Display Name *</Label>
            <Input
              id="cat-label"
              value={categoryForm.label}
              onChange={(e) => setCategoryForm({ ...categoryForm, label: e.target.value })}
              placeholder="e.g., Chef's Specials"
            />
          </div>

          <div>
            <Label htmlFor="cat-key">Key *</Label>
            <Input
              id="cat-key"
              value={categoryForm.key}
              onChange={(e) =>
                setCategoryForm({ ...categoryForm, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })
              }
              placeholder="e.g., chef_specials"
            />
          </div>

          <div>
            <Label htmlFor="cat-desc">Description</Label>
            <Textarea
              id="cat-desc"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="cat-emoji">Smart Fridge Emoji</Label>
            <Input
              id="cat-emoji"
              value={categoryForm.emoji || ""}
              onChange={(e) => setCategoryForm({ ...categoryForm, emoji: e.target.value })}
              placeholder="e.g., 🥗 or 🍝"
              maxLength={4}
              data-testid="category-emoji-input"
            />
            <p className="text-xs text-gray-500 mt-1">Displayed on Smart Fridge shelves for this category.</p>
          </div>

          <div>
            <Label>Color</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setCategoryForm({ ...categoryForm, color })}
                  className={`w-7 h-7 rounded-full transition-transform ${
                    categoryForm.color === color ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : ""
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>Category Image (optional)</Label>
            <div
              className="mt-2 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-[#1aa59a] transition-colors"
              onClick={() => document.getElementById("cat-image-input").click()}
            >
              {categoryForm.image_base64 ? (
                <img
                  src={categoryForm.image_base64}
                  alt="Preview"
                  className="w-12 h-12 mx-auto rounded-full object-cover"
                />
              ) : (
                <div className="text-gray-400">
                  <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                  <p className="text-xs">Upload icon</p>
                </div>
              )}
            </div>
            <input
              id="cat-image-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleImageUpload(e, (img) => setCategoryForm({ ...categoryForm, image_base64: img }))}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="cat-rotating" className="cursor-pointer">
                Rotating Menu
              </Label>
              <p className="text-xs text-gray-500 mt-0.5">Items rotate weekly (Week 1, 2, 3)</p>
            </div>
            <Switch
              id="cat-rotating"
              checked={categoryForm.is_rotating}
              onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_rotating: checked })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#1aa59a] hover:bg-[#158a80]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCategory ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
